import { Link, useParams, useNavigate } from "react-router-dom";
import { SupportDialog } from "@/components/support/support-dialog";
import { useQuery, useMutation } from "@apollo/client/react";
import {
  ArrowLeft,
  Pencil,
  BookOpen,
  ClipboardList,
  Trophy,
  Flame,
  Loader2,
  Trash2,
  Check,
  X,
  SkipForward,
  Star,
  User,
  FileText,
  LifeBuoy,
} from "lucide-react";

import { GET_STUDENT, GET_STUDENTS } from "@/graphql/queries/students";
import { DELETE_STUDENT } from "@/graphql/mutations/students";
import { AvatarActions, useAvatarStudio } from "@/components/avatar-studio";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Progress } from "@/components/ui/progress";
import { ageLabel, gradeLabel } from "@/lib/students";
import { SkillSetupNotice } from "@/components/skills/setup-notice";
import { formatFullDate, formatRelativeDate } from "@/lib/relative-date";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function StudentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, loading, refetch: refetchStudent } = useQuery(GET_STUDENT, {
    variables: { id },
    skip: !id,
  });

  const studio = useAvatarStudio(id, { onChanged: () => refetchStudent() });

  const [deleteStudent, { loading: isDeleting }] = useMutation(DELETE_STUDENT, {
    refetchQueries: [{ query: GET_STUDENTS }],
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const student = data?.student;

  if (!student) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Mokinys nerastas</p>
        <Button asChild className="mt-4">
          <Link to="/students">Grįžti į sąrašą</Link>
        </Button>
      </div>
    );
  }

  const level = student.level;

  async function handleDelete() {
    try {
      const { data } = await deleteStudent({ variables: { id } });
      if (data?.deleteStudent?.success) {
        toast.success("Mokinys ištrintas");
        navigate("/students");
      } else {
        toast.error("Nepavyko ištrinti mokinio");
      }
    } catch {
      toast.error("Nepavyko ištrinti mokinio");
    }
  }

  const initials = student.name
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="space-y-6">
      {!student.skillSetupDone && (
        <SkillSetupNotice
          studentId={student.id}
          name={student.name}
          grade={student.grade}
          gradeKnown={student.gradeKnown}
        />
      )}

      {/* Hero Section — Avatar is the star */}
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-amber-500/10 dark:from-purple-500/20 dark:via-pink-500/20 dark:to-amber-500/20">
        {/* Decorative background blobs */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-purple-400/30 to-pink-400/30 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-amber-400/30 to-orange-400/30 blur-3xl" />

        {/* Top bar: back + actions */}
        <div className="relative flex items-center justify-between p-4 sm:p-6">
          <Button variant="ghost" size="icon" className="rounded-full backdrop-blur-sm bg-background/40 hover:bg-background/70" asChild>
            <Link to="/students">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full backdrop-blur-sm bg-background/60" asChild>
              <Link to={`/students/${student.id}/edit`}>
                <Pencil className="mr-2 h-4 w-4" />
                Redaguoti
              </Link>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full text-destructive hover:bg-destructive/10" disabled={isDeleting}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Ištrinti mokinį?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Bus ištrintas mokinys „{student.name}" ir visi susiję duomenys
                    (sesijos, pasiekimai, įgūdžiai). Šio veiksmo negalima atšaukti.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Atšaukti</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>
                    Ištrinti
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        {/* Hero content: giant avatar + name */}
        <div className="relative flex flex-col items-center gap-6 px-6 pb-10 pt-4 sm:flex-row sm:items-center sm:gap-10 sm:px-10 sm:pb-14">
          <div className="relative flex flex-col items-center gap-3">
            {/* Glow ring behind avatar */}
            <div className="absolute inset-0 -z-10 scale-110 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 opacity-30 blur-2xl" />
            <div className="rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-amber-500 p-1.5 shadow-2xl">
              <Avatar className="h-56 w-56 border-4 border-background sm:h-64 sm:w-64 md:h-72 md:w-72">
                {student.avatarUrl ? (
                  <AvatarImage src={student.avatarUrl} alt={student.name} className="object-cover" />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-6xl text-white sm:text-7xl">
                  {initials || <User className="h-24 w-24" />}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Avatar actions */}
            <AvatarActions studio={studio} />
          </div>

          {/* Name + quick facts */}
          <div className="flex flex-1 flex-col items-center text-center sm:items-start sm:text-left">
            {level && (
              <div className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                <span className="text-base leading-none">{level.icon}</span>
                <span>{level.name}</span>
              </div>
            )}
            <h1 className="bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-4xl font-extrabold tracking-tight text-transparent sm:text-5xl md:text-6xl">
              {student.name}
            </h1>
            {/* Both are unknown until somebody says when this learner was born,
                and an empty line reads as a missing value rather than none. */}
            {(student.age != null || student.grade != null) && (
              <p className="mt-2 text-lg text-muted-foreground">
                {student.age != null && ageLabel(student.age)}
                {student.grade != null && (
                  <>
                    {student.age != null && <span className="mx-1.5">·</span>}
                    {gradeLabel(student.grade)}
                  </>
                )}
              </p>
            )}
            {student.notes && (
              <p className="mt-3 max-w-xl text-sm text-muted-foreground">
                {student.notes}
              </p>
            )}

            {/* Facts, not buttons. These used to be bordered, shadowed pills in
                the same rounded shape as „Nauja sesija" and the two outline
                buttons under them — six look-alike capsules, half of which did
                nothing when pressed. Plain text with separators reads as
                something to look at, which leaves the buttons as the only
                things that look pressable. */}
            <dl className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm sm:justify-start">
              <div className="inline-flex items-center gap-1.5">
                <dt className="sr-only">Taškai</dt>
                <span aria-hidden>💎</span>
                <dd className="font-semibold">{student.totalPoints?.toLocaleString()}</dd>
                <span className="text-muted-foreground">taškų</span>
              </div>
              <span aria-hidden className="text-muted-foreground/50">·</span>
              <div className="inline-flex items-center gap-1.5">
                <dt className="sr-only">Serija</dt>
                <Flame aria-hidden className="h-4 w-4 text-orange-500" />
                <dd className="font-semibold">{student.currentStreak}</dd>
                <span className="text-muted-foreground">dienų serija</span>
              </div>
              <span aria-hidden className="text-muted-foreground/50">·</span>
              <div className="inline-flex items-center gap-1.5">
                <dt className="sr-only">Pasiekimai</dt>
                <Trophy aria-hidden className="h-4 w-4 text-amber-500" />
                <dd className="font-semibold">{student.achievements?.length ?? 0}</dd>
                <span className="text-muted-foreground">pasiekimų</span>
              </div>
            </dl>

            {/* Primary actions */}
            <div className="mt-7 flex flex-wrap justify-center gap-2 sm:justify-start">
              <Button size="lg" className="rounded-full shadow-md" asChild>
                <Link to={`/sessions/new?studentId=${student.id}`}>
                  <ClipboardList className="mr-2 h-4 w-4" />
                  Nauja sesija
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full bg-background/70 backdrop-blur-sm" asChild>
                <Link to={`/students/${student.id}/skills`}>
                  <BookOpen className="mr-2 h-4 w-4" />
                  Įgūdžiai
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="rounded-full bg-background/70 backdrop-blur-sm" asChild>
                <Link to={`/students/${student.id}/reports`}>
                  <FileText className="mr-2 h-4 w-4" />
                  Ataskaitos
                </Link>
              </Button>

              {/* Deliberately NOT a fourth pill. The three above are the day's
                  work; help is what you reach for when something is wrong with
                  it, and at the same weight it competes with them — the rule the
                  learner's help ladder follows one screen over.

                  Worth having here at all because it names the learner: a
                  request opened from this page attaches them, which answers
                  „kuris vaikas" before an admin has to ask. Only the ID travels,
                  and only onto the row — never into the prompt. */}
              <SupportDialog
                learnerId={student.id}
                trigger={
                  <Button
                    variant="ghost"
                    size="sm"
                    className="self-center rounded-full text-muted-foreground hover:text-foreground"
                  >
                    <LifeBuoy className="mr-1.5 h-3.5 w-3.5" />
                    Kažkas ne taip?
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* Gamification Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-yellow-500/10" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lygis</CardTitle>
            <span className="text-2xl">{level?.icon}</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{level?.name}</div>
            {level?.nextLevel && (
              <div className="mt-2 space-y-1">
                <Progress value={level.progressToNextLevel} className="h-1.5" />
                <p className="text-xs text-muted-foreground">
                  {level.pointsToNextLevel} iki {level.nextLevel.name}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taškai</CardTitle>
            <span className="text-2xl">💎</span>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.totalPoints?.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Viso surinkta</p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-red-500/10" />
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Serija</CardTitle>
            <Flame className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.currentStreak}</div>
            <p className="text-xs text-muted-foreground">
              Rekordas: {student.longestStreak} d.
            </p>
          </CardContent>
        </Card>

        <Link to={`/students/${student.id}/achievements`} className="block">
          <Card className="relative overflow-hidden transition-colors hover:border-amber-500/50">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pasiekimai</CardTitle>
              <Trophy className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {student.achievements?.length ?? 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Peržiūrėti visus &rarr;
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Recent Sessions */}
      <Card>
        <CardHeader>
          <CardTitle>Paskutinės sesijos</CardTitle>
          <CardDescription>Naujausios mokymosi sesijos</CardDescription>
        </CardHeader>
        <CardContent>
          {!student.sessions || student.sessions.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nėra sesijų. Sukurkite pirmąją sesiją!
            </p>
          ) : (
            <div className="space-y-3">
              {[...student.sessions]
                .sort((a: any, b: any) => {
                  const aDone = a.status === "COMPLETED";
                  const bDone = b.status === "COMPLETED";
                  if (aDone !== bDone) return aDone ? 1 : -1;
                  const aDate = a.completedAt || a.createdAt;
                  const bDate = b.completedAt || b.createdAt;
                  return new Date(bDate).getTime() - new Date(aDate).getTime();
                })
                .map((session: any) => {
                  const displayDate = session.completedAt || session.createdAt;
                  return (
                  <div
                    key={session.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex flex-col gap-1">
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm text-muted-foreground cursor-default w-fit">
                              {formatRelativeDate(displayDate)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{formatFullDate(displayDate)}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      {session.totalCount > 0 && (
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          {session.pointsEarned > 0 && (
                            <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                              <Star className="h-3 w-3" />
                              {session.pointsEarned}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                            <Check className="h-3 w-3" />
                            {session.correctCount}
                          </span>
                          {session.incorrectCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                              <X className="h-3 w-3" />
                              {session.incorrectCount}
                            </span>
                          )}
                          {session.skippedCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-gray-500">
                              <SkipForward className="h-3 w-3" />
                              {session.skippedCount}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={session.status === "COMPLETED" ? "default" : "secondary"}>
                        {session.status === "DRAFT" && "Juodraštis"}
                        {session.status === "GENERATED" && "Sugeneruota"}
                        {session.status === "GRADED" && "Įvertinta"}
                        {session.status === "COMPLETED" && "Užbaigta"}
                      </Badge>
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/sessions/${session.id}`}>Peržiūrėti</Link>
                      </Button>
                    </div>
                  </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
